import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  Chip,
  IconButton,
  Grid,
  InputAdornment,
} from '@mui/material';
import { Add, PictureAsPdf, Visibility, Search } from '@mui/icons-material';
import { Abastecimento, FechamentoAbastecimento, PostoCombustivel } from '../types';
import { fechamentoService } from '../services/fechamentoService';
import { abastecimentoService } from '../services/abastecimentoService';
import { postoService } from '../services/postoService';
import { getErrorMessage } from '../utils/errors';
import { useAuth } from '../contexts/AuthContext';
import PdfViewerDialog from '../components/PdfViewerDialog';

const Fechamentos: React.FC = () => {
  const { hasRole } = useAuth();
  const isGestor = hasRole('ADMIN') || hasRole('GERENTE');
  const [fechamentos, setFechamentos] = useState<FechamentoAbastecimento[]>([]);
  const [abastecimentosAbertos, setAbastecimentosAbertos] = useState<Abastecimento[]>([]);
  const [postos, setPostos] = useState<PostoCombustivel[]>([]);
  const [open, setOpen] = useState(false);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [fechamentoSelecionado, setFechamentoSelecionado] = useState<FechamentoAbastecimento | null>(null);
  const [pdfViewer, setPdfViewer] = useState<{ url: string; title: string } | null>(null);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({ dataInicio: '', dataFim: '', postoCombustivel: '', observacao: '' });
  const [filtro, setFiltro] = useState('');

  useEffect(() => {
    carregar();
  }, []);

  const carregar = async () => {
    try {
      const [lista, abasts, postosData] = await Promise.all([
        fechamentoService.listar(),
        abastecimentoService.listar(),
        postoService.listarAtivos(),
      ]);
      setFechamentos(lista);
      setAbastecimentosAbertos(abasts.filter(a => (a as any).situacaoPagamento === 'ABERTO'));
      setPostos(postosData);
    } catch (e) {
      console.error(e);
    }
  };

  const abrirNovo = () => {
    const posto = postos[0]?.nome || '';
    const abertosDoPosto = abastecimentosAbertos.filter(a => a.postoCombustivel === posto);
    const datas = abertosDoPosto
      .map(a => (a.dataAbastecimento ? new Date(a.dataAbastecimento).toISOString().slice(0, 10) : ''))
      .filter(Boolean)
      .sort();
    setForm({
      dataInicio: datas.length ? datas[0] : '',
      dataFim: datas.length ? datas[datas.length - 1] : '',
      postoCombustivel: posto,
      observacao: '',
    });
    setErro('');
    setOpen(true);
  };

  const filtrarPorPostoPeriodo = () => {
    if (!form.postoCombustivel || !form.dataInicio || !form.dataFim) return [];
    return abastecimentosAbertos.filter(a => {
      const data = a.dataAbastecimento ? new Date(a.dataAbastecimento).toISOString().slice(0, 10) : '';
      return a.postoCombustivel === form.postoCombustivel && data >= form.dataInicio && data <= form.dataFim;
    });
  };

  const selecionados = filtrarPorPostoPeriodo();

  const handleFechar = async () => {
    if (!form.dataInicio || !form.dataFim || !form.postoCombustivel) {
      setErro('Preencha período e posto');
      return;
    }
    if (selecionados.length === 0) {
      setErro('Não há abastecimentos ABERTOS para este período e posto');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      const ids = selecionados.map(s => s.id!).filter(Boolean);
      await fechamentoService.fechar({
        dataInicio: form.dataInicio,
        dataFim: form.dataFim,
        postoCombustivel: form.postoCombustivel,
        abastecimentoIds: ids,
        observacao: form.observacao || undefined,
      });
      setOpen(false);
      carregar();
    } catch (e) {
      setErro(getErrorMessage(e));
    } finally {
      setSalvando(false);
    }
  };

  const abrirDetalhe = async (f: FechamentoAbastecimento) => {
    try {
      const completo = await fechamentoService.buscarPorId(f.id!);
      setFechamentoSelecionado(completo);
      setDetalheOpen(true);
    } catch (e) {
      console.error(e);
    }
  };

  const abrirPdf = (blob: Blob, titulo: string) => {
    const url = window.URL.createObjectURL(blob);
    setPdfViewer((prev) => {
      if (prev) window.URL.revokeObjectURL(prev.url);
      return { url, title: titulo };
    });
  };

  const fecharPdfViewer = () => {
    setPdfViewer((prev) => {
      if (prev) window.URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const visualizarPdf = async (id: number) => {
    try {
      const blob = await fechamentoService.gerarRelatorioPdf(id);
      abrirPdf(blob, `Fechamento ${id}`);
    } catch (e) {
      console.error(e);
      setErro(getErrorMessage(e));
    }
  };

  const formatarMoeda = (v: number) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const formatarLitros = (v: number) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

  const termo = filtro.trim().toLowerCase();
  const fechamentosFiltrados = termo
    ? fechamentos.filter((item) => Object.values(item).filter(Boolean).some((campo) => String(campo).toLowerCase().includes(termo)))
    : fechamentos;

  return (
    <Container disableGutters maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Fechamentos de Abastecimentos</Typography>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          {isGestor && <Button variant="contained" startIcon={<Add />} onClick={abrirNovo}>Novo Fechamento</Button>}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nº Controle</TableCell>
              <TableCell>Posto</TableCell>
              <TableCell>Período</TableCell>
              <TableCell align="right">Qtd.</TableCell>
              <TableCell align="right">Litros</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Fechado por</TableCell>
              <TableCell>Data Fechamento</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fechamentosFiltrados.map(f => (
              <TableRow key={f.id}>
                <TableCell><strong>{f.numeroControle}</strong></TableCell>
                <TableCell>{f.postoCombustivel}</TableCell>
                <TableCell>{f.dataInicio} a {f.dataFim}</TableCell>
                <TableCell align="right">{f.quantidadeAbastecimentos}</TableCell>
                <TableCell align="right">{formatarLitros(f.totalLitros)}</TableCell>
                <TableCell align="right">{formatarMoeda(f.totalValor)}</TableCell>
                <TableCell><Chip label="EMISSÃO DE NOTA" color="warning" size="small" /></TableCell>
                <TableCell>{f.fechadoPorNome}</TableCell>
                <TableCell>{f.dataFechamento ? new Date(f.dataFechamento).toLocaleString('pt-BR') : '-'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => abrirDetalhe(f)} title="Visualizar"><Visibility /></IconButton>
                  <IconButton size="small" onClick={() => visualizarPdf(f.id!)} title="Relatório PDF"><PictureAsPdf /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Novo Fechamento por Período</DialogTitle>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Posto" value={form.postoCombustivel} onChange={e => {
                const posto = e.target.value;
                const abertosDoPosto = abastecimentosAbertos.filter(a => a.postoCombustivel === posto);
                const datas = abertosDoPosto
                  .map(a => (a.dataAbastecimento ? new Date(a.dataAbastecimento).toISOString().slice(0, 10) : ''))
                  .filter(Boolean)
                  .sort();
                setForm({ ...form, postoCombustivel: posto, dataInicio: datas.length ? datas[0] : form.dataInicio, dataFim: datas.length ? datas[datas.length - 1] : form.dataFim });
              }}>
                {postos.map(p => <MenuItem key={p.id} value={p.nome}>{p.nome}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField fullWidth type="date" label="Data Início" InputLabelProps={{ shrink: true }} value={form.dataInicio} onChange={e => setForm({...form, dataInicio: e.target.value})} />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField fullWidth type="date" label="Data Fim" InputLabelProps={{ shrink: true }} value={form.dataFim} onChange={e => setForm({...form, dataFim: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observação" value={form.observacao} onChange={e => setForm({...form, observacao: e.target.value})} />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            {form.dataInicio && form.dataFim
              ? `Abastecimentos ABERTOS encontrados: ${selecionados.length}`
              : 'Selecione o período (data início e fim) e o posto para listar os abastecimentos abertos.'}
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 1, maxHeight: 300 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Placa</TableCell>
                  <TableCell>Motorista</TableCell>
                  <TableCell align="right">Litros</TableCell>
                  <TableCell align="right">Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selecionados.map(a => (
                  <TableRow key={a.id}>
                    <TableCell>{a.dataAbastecimento ? new Date(a.dataAbastecimento).toLocaleDateString('pt-BR') : '-'}</TableCell>
                    <TableCell>{a.veiculoPlaca}</TableCell>
                    <TableCell>{a.motoristaNome}</TableCell>
                    <TableCell align="right">{formatarLitros(a.litrosAbastecidos)}</TableCell>
                    <TableCell align="right">{formatarMoeda(a.valorTotal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleFechar} disabled={salvando}>{salvando ? 'Fechando...' : 'Fechar e Emitir para Pagamento'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detalheOpen} onClose={() => setDetalheOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Fechamento {fechamentoSelecionado?.numeroControle}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}><Typography variant="body2"><strong>Posto:</strong> {fechamentoSelecionado?.postoCombustivel}</Typography></Grid>
            <Grid item xs={6} md={3}><Typography variant="body2"><strong>Período:</strong> {fechamentoSelecionado?.dataInicio} a {fechamentoSelecionado?.dataFim}</Typography></Grid>
            <Grid item xs={6} md={3}><Typography variant="body2"><strong>Total:</strong> {formatarMoeda(fechamentoSelecionado?.totalValor || 0)}</Typography></Grid>
            <Grid item xs={6} md={3}><Typography variant="body2"><strong>Litros:</strong> {formatarLitros(fechamentoSelecionado?.totalLitros || 0)}</Typography></Grid>
          </Grid>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Placa</TableCell>
                  <TableCell>Motorista</TableCell>
                  <TableCell align="right">Litros</TableCell>
                  <TableCell align="right">V. Unit.</TableCell>
                  <TableCell align="right">V. Total</TableCell>
                  <TableCell>Posto</TableCell>
                  <TableCell align="right">KM</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fechamentoSelecionado?.abastecimentos?.map(a => (
                  <TableRow key={a.id}>
                    <TableCell>{a.dataAbastecimento ? new Date(a.dataAbastecimento).toLocaleDateString('pt-BR') : '-'}</TableCell>
                    <TableCell>{a.veiculoPlaca}</TableCell>
                    <TableCell>{a.motoristaNome}</TableCell>
                    <TableCell align="right">{formatarLitros(a.litrosAbastecidos)}</TableCell>
                    <TableCell align="right">{formatarMoeda(a.valorUnitario || 0)}</TableCell>
                    <TableCell align="right">{formatarMoeda(a.valorTotal)}</TableCell>
                    <TableCell>{a.postoCombustivel}</TableCell>
                    <TableCell align="right">{a.quilometragemNoMomento?.toLocaleString('pt-BR')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => visualizarPdf(fechamentoSelecionado?.id!)} startIcon={<PictureAsPdf />}>Gerar Relatório PDF</Button>
          <Button onClick={() => setDetalheOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <PdfViewerDialog
        open={!!pdfViewer}
        url={pdfViewer?.url ?? null}
        title={pdfViewer?.title}
        onClose={fecharPdfViewer}
      />
    </Container>
  );
};

export default Fechamentos;
